import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import { fnUrl } from "../../../utils/fnUrl";
import { db } from "../utils/firebase";
import { LOCALES, type Locale, getLocalizedText, setLocaleValue } from "../utils/locale";

interface Tip {
  id: string;
  title: Record<string, string>;
  text: Record<string, string>;
}

const TipEditor = () => {
  const { project } = useParams<{ project: string }>();
  const { user, firebaseUser } = useAuth();

  const [tips, setTips] = useState<Tip[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [locale, setLocale] = useState<Locale>("en");
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [translating, setTranslating] = useState(false);
  const [translateProgress, setTranslateProgress] = useState(0);
  const [translateTotal, setTranslateTotal] = useState(0);

  const loadTips = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "content", project, "tips"));
      const loaded: Tip[] = [];
      snap.forEach((s) => {
        if (s.id.startsWith("_")) return;
        const d = s.data();
        loaded.push({
          id: s.id,
          title: typeof d.title === "object" ? d.title : { en: d.title || "" },
          text: typeof d.text === "object" ? d.text : { en: d.text || "" },
        });
      });
      loaded.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
      setTips(loaded);
      setDirtyIds(new Set());
      setNewIds(new Set());
      setDirty(false);

    } catch (err) {
      console.error("[TipEditor] Load error:", err);
    }
    setLoading(false);
  }, [project]);

  useEffect(() => { loadTips(); }, [loadTips]);

  const updateTip = (index: number, field: "title" | "text", value: string) => {
    setTips((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: setLocaleValue(next[index][field], locale, value) };
      return next;
    });
    setDirtyIds((prev) => new Set(prev).add(tips[index].id));
    setDirty(true);

  };

  const addTip = () => {
    const nextNum = tips.length + 1;
    const id = `tip_${nextNum}`;
    setTips((prev) => [...prev, { id, title: { en: "" }, text: { en: "" } }]);
    setNewIds((prev) => new Set(prev).add(id));
    setDirty(true);

  };

  const removeTip = async (index: number) => {
    const tip = tips[index];
    if (!window.confirm(`Delete "${getLocalizedText(tip.title, "en") || tip.id}"?`)) return;
    if (!newIds.has(tip.id) && project) {
      await deleteDoc(doc(db, "content", project, "tips", tip.id));
    }
    setTips((prev) => prev.filter((_, i) => i !== index));
    setNewIds((prev) => { const n = new Set(prev); n.delete(tip.id); return n; });
    setDirtyIds((prev) => { const n = new Set(prev); n.delete(tip.id); return n; });
  };

  const save = async () => {
    if (!project) return;
    setSaving(true);
    try {
      // Write only changed/new tips
      const toWrite = tips.filter((t) => dirtyIds.has(t.id) || newIds.has(t.id));
      for (const tip of toWrite) {
        await setDoc(doc(db, "content", project, "tips", tip.id), {
          title: tip.title,
          text: tip.text,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        });
      }
      setDirtyIds(new Set());
      setNewIds(new Set());
      toast.success("Saved");
      setDirty(false);
    } catch (err) {
      console.error("[TipEditor] Save error:", err);
      toast.error("Error saving");
    }
    setSaving(false);
  };

  const hasLocaleContent = (loc: Locale): boolean => {
    return tips.some((t) => (t.title[loc]?.trim() || t.text[loc]?.trim()));
  };

  const handleTranslate = async () => {
    if (!project || !firebaseUser) return;
    // Collect all English texts: title + text for each tip
    const tasks: { tipIdx: number; field: "title" | "text"; en: string }[] = [];
    tips.forEach((tip, i) => {
      const titleEn = tip.title.en?.trim();
      const textEn = tip.text.en?.trim();
      if (titleEn) tasks.push({ tipIdx: i, field: "title", en: titleEn });
      if (textEn) tasks.push({ tipIdx: i, field: "text", en: textEn });
    });
    if (tasks.length === 0) { toast.info("Nothing to translate"); return; }

    setTranslating(true);
    setTranslateProgress(0);
    setTranslateTotal(tasks.length);
    try {
      const idToken = await firebaseUser.getIdToken();
      const url = fnUrl("translateContent");
      const results: { tipIdx: number; field: "title" | "text"; locales: Record<string, string> }[] = [];
      let completed = 0;
      const MAX_CONCURRENT = 5;

      for (let i = 0; i < tasks.length; i += MAX_CONCURRENT) {
        const chunk = tasks.slice(i, i + MAX_CONCURRENT);
        await Promise.all(
          chunk.map(async (task) => {
            const resp = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
              body: JSON.stringify({ key: `tip.${task.tipIdx}.${task.field}`, text: task.en }),
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            results.push({ tipIdx: task.tipIdx, field: task.field, locales: data.locales });
            completed++;
            setTranslateProgress(completed);
          })
        );
      }

      // Apply translations and auto-save
      const updated = tips.map((tip, i) => {
        let t = { ...tip };
        for (const r of results.filter((r) => r.tipIdx === i)) {
          t = { ...t, [r.field]: { ...t[r.field], ...r.locales } };
        }
        return t;
      });

      // Save all tips to Firestore
      for (const tip of updated) {
        await setDoc(doc(db, "content", project, "tips", tip.id), {
          title: tip.title,
          text: tip.text,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || "unknown",
        });
      }

      setTips(updated);
      setDirtyIds(new Set());
      setNewIds(new Set());
      setDirty(false);
      toast.success(`Translated & saved ${results.length} fields`);
    } catch (err: any) {
      console.error("[TipEditor] Translate error:", err);
      toast.error(`Translation failed: ${err.message}`);
    }
    setTranslating(false);
  };

  if (loading) return <div className="editor__loading">Loading...</div>;

  return (
    <div className="editor">
      <div className="editor__header">
        <span className="editor__doc-id">Tips ({tips.length})</span>
        <div className="editor__actions">
          <button
            className="appsettings__translate-btn"
            onClick={handleTranslate}
            disabled={translating || tips.length === 0}
          >
            {translating ? `${translateProgress}/${translateTotal}` : "Translate"}
          </button>
          <button className="editor__save-btn" onClick={save} disabled={saving || !dirty}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="editor__locale-tabs">
        {LOCALES.map((loc) => (
          <button
            key={loc}
            className={`editor__locale-tab${locale === loc ? " editor__locale-tab--active" : ""}${!hasLocaleContent(loc) ? " editor__locale-tab--missing" : ""}`}
            onClick={() => setLocale(loc)}
          >
            {loc.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="tip-editor">
        {tips.map((tip, i) => (
          <div key={tip.id} className="tip-editor__row">
            <span className="tip-editor__num">{i + 1}</span>
            <input
              className="tip-editor__input"
              value={getLocalizedText(tip.title, locale)}
              onChange={(e) => updateTip(i, "title", e.target.value)}
              placeholder="Title..."
            />
            <textarea
              className="tip-editor__textarea"
              value={getLocalizedText(tip.text, locale)}
              onChange={(e) => updateTip(i, "text", e.target.value)}
              placeholder="Text..."
              rows={2}
            />
            <button className="tip-editor__delete" onClick={() => removeTip(i)} title="Delete">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        ))}
        <button className="tip-editor__add" onClick={addTip}>+ Add Tip</button>
      </div>

      {translating && (
        <div className="translate-overlay">
          <div className="translate-overlay__card">
            <div className="translate-overlay__icon">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
                <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/>
                <path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
              </svg>
            </div>
            <p className="translate-overlay__counter">{translateProgress} / {translateTotal}</p>
            <div className="translate-overlay__track">
              <div
                className="translate-overlay__fill"
                style={{ width: `${translateTotal ? (translateProgress / translateTotal) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipEditor;
